import { BUTTON_SIZE, DSButton, DSModal } from '@repo/design-system/index'
import { QIcon_chevron_bold_right } from '@repo/icons/index'
import { useState } from 'react'
import {
  AnalysisComponentDetailMetrics,
  CAF_ANALYSIS_TYPE_MAP,
  ComplexityDetailItem,
} from '../../../../../model/caf.interface'
import { useCafMetricsDetailsV5 } from '../../../../../modules/apis/webview/caf'
import useAppRouter from '../../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import useWebviewNavigation from '../../../../../modules/hooks/webview/common/useWebviewNavigation'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import urls from '../../../../../modules/urls'
import Accordion from '../../common/Accordion'
interface PronunciationDetailItem extends ComplexityDetailItem {
  errors: string[]
  error_tag: number
  error_type: string
}
const PronunciationArticulationAccordion = ({ data }: { data: AnalysisComponentDetailMetrics }) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const router = useAppRouter()
  const { lessonId, tab, componentId, metricTag, metricIndex } = router.query
  const { metric_id, metric_tag } = data
  const { data: detaildata } = useCafMetricsDetailsV5(metric_id, metric_tag)
  const { navigate } = useWebviewNavigation()
  const goToDetailPage = (metricId, title, level_tag) => () => {
    if (isMobile) {
      navigate({
        url: urls.webview.caf.analysis.detail(CAF_ANALYSIS_TYPE_MAP[1], metricId, level_tag),
      })
    } else {
      router.push(
        urls.student.newPortal.lessons.reviewDetail.asCafComplexityMetricDetail(
          lessonId,
          componentId,
          metricTag,
          metricId,
          level_tag,
          title
        )
      )
    }
  }

  const AccordionTitle = ({ detail }) => {
    return (
      <div className={`row-auto flex items-center justify-start pr-[4px]`}>
        <span className={`mr-[8px] text-[14px] font-medium text-black sm:text-[16px]`}>
          {t(n_caf.pronunciation.error(detail.right, detail.wrong))}
        </span>
        <div className="rounded-[4px] bg-gray-100 py-[2px] px-[6px] text-[10px] text-gray-500 sm:text-[12px]">
          {t(n_caf.pronunciation[`label_${detail.level}`])}
        </div>
      </div>
    )
  }
  const isNoDetail = !data?.details || data.details.length === 0

  if (isMobile) {
    return (
      <>
        <ArticulationDetailListHeader />
        {isNoDetail ? (
          <NoDetail />
        ) : (
          <div className="mt-[16px]">
            {data?.details?.map((detail: PronunciationDetailItem, idx) => (
              <Accordion key={idx} title={<AccordionTitle detail={detail} />} idx={idx + 1} className={'mb-[8px]'}>
                <span className="text-12 text-gray-500">ⓘ {t(n_caf.complexity.vocab_info)}</span>
                <div className="mt-16 flex flex-wrap">
                  {detail.errors?.slice(0, 15)?.map((item) => (
                    <div
                      key={item}
                      className="border-1 mr-8 mb-8 items-center justify-center rounded-[50px] bg-gray-50 px-8 py-4"
                    >
                      <span className="text-[14px] text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </Accordion>
            ))}
          </div>
        )}
      </>
    )
  } else {
    return (
      <>
        <ArticulationDetailListHeader />
        {isNoDetail ? (
          <NoDetail />
        ) : (
          <div className="mt-[16px]">
            {data?.details?.map((detail: PronunciationDetailItem, idx) => (
              <Accordion
                key={idx}
                title={<AccordionTitle detail={detail} />}
                idx={idx + 1}
                className={'mb-[8px] cursor-pointer'}
              >
                <div className="mt-16 flex cursor-default flex-wrap">
                  {detail.errors?.slice(0, 15)?.map((item) => (
                    <div
                      key={item}
                      className="border-1 mr-8 mb-8 items-center justify-center rounded-[50px] bg-gray-50 px-8 py-4"
                    >
                      <span className="text-14 text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </Accordion>
            ))}
          </div>
        )}
      </>
    )
  }
}

export const ArticulationDetailListHeader = () => {
  const { t } = useTranslation()
  const [showPopup, setShowPopup] = useState(false)
  return (
    <div className="mt-[32px] flex flex-col">
      <div className="text-[16px] font-bold text-gray-700 sm:text-[18px]">{t(n_caf.pronunciation.section_title)}</div>
      <div className="text-[14px] font-normal text-gray-500">{t(n_caf.pronunciation.section_detail)}</div>
      <div
        className="mt-[12px] flex w-full cursor-pointer flex-row items-center justify-end text-[14px] font-bold text-gray-500"
        onClick={() => setShowPopup(true)}
      >
        {t(n_caf.pronunciation.section_btn)}
        <QIcon_chevron_bold_right className="ml-[2px] mb-[2px]" width={16} height={16} />
      </div>
      <DSModal id="TextOnlyMOneButtonPopup" isOpen={showPopup} setIsOpen={setShowPopup} size={'lg'}>
        <DSModal.Title>{t(n_caf.pronunciation.metric1_popup1.title)}</DSModal.Title>
        <DSModal.Content>
          <div className="text-[16px] text-gray-700">{t(n_caf.pronunciation.metric1_popup1.body)}</div>
          <div className="relative mt-[32px] flex flex-row items-center justify-start pl-[15px]">
            <span className="keep-all ml-[4px] font-normal">
              <span className="whitespace-nowrap font-bold">{t(n_caf.pronunciation.label_1)} : </span>
              {t(n_caf.pronunciation.metric1_popup1.body2)}
            </span>
            <span className="absolute left-[5px] top-0">• </span>
          </div>
          <div className="relative flex flex-row items-center justify-start pl-[15px]">
            <span className="keep-all ml-[4px] font-normal">
              <span className="whitespace-nowrap font-bold">{t(n_caf.pronunciation.label_2)} : </span>
              {t(n_caf.pronunciation.metric1_popup1.body3)}
            </span>
            <span className="absolute left-[5px] top-0">• </span>
          </div>
          <div className="relative mb-[100px] flex flex-row items-center justify-start pl-[15px]">
            <span className="keep-all ml-[4px] font-normal">
              <span className="whitespace-nowrap font-bold">{t(n_caf.pronunciation.label_3)} : </span>
              {t(n_caf.pronunciation.metric1_popup1.body4)}
            </span>
            <span className="absolute left-[5px] top-0">• </span>
          </div>
        </DSModal.Content>
        <DSModal.ButtonContainer direction="horizontal">
          <DSButton size={BUTTON_SIZE.MD} onClick={() => setShowPopup(false)}>
            <DSButton.Label>{t(n_caf.highlight.okay)}</DSButton.Label>
          </DSButton>
        </DSModal.ButtonContainer>
      </DSModal>
    </div>
  )
}

const NoDetail = () => {
  const { t } = useTranslation()
  return (
    <div className="border-1 mt-[16px] flex h-[120px] w-full items-center justify-center rounded-[8px] border-gray-100 bg-white text-[14px] text-gray-500">
      {t(n_caf.pronunciation.metric1_empty_msg)}
    </div>
  )
}
export default PronunciationArticulationAccordion
